import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter, finalize,
  map,
  share,
  shareReplay,
  switchMap,
} from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AccountsContextService } from '../accounts/account-context.service';
import { HttpUtilsService } from '../shared/http-utils/http-utils.service';
import { ItemResponseWrapper } from '../shared/http.types';
import { ICreateAccount } from '../shared/account/create-account.helper';
import { FeatureFlags } from './feature-flags.types';

/**
 * (!!!!) Do not use instance of this class directly in component
 * It should be always provider for FeatureFlagService. And the last one can be used in components.
 */
@Injectable()
export class FeatureFlagsProviderService {
  private readonly _isPendingState$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private readonly accountsFeatureFlags$: Observable<FeatureFlags.IEntity>;

  private readonly accountContext: AccountsContextService;
  private readonly httpClient: HttpClient;
  private readonly httpUtils: HttpUtilsService;

  constructor() {
    this.accountContext = inject(AccountsContextService);
    this.httpClient = inject(HttpClient);
    this.httpUtils = inject(HttpUtilsService);

    this.accountsFeatureFlags$ = this.accountContext.account$
      .pipe(
        filter(account => !!account.id),
        distinctUntilChanged(),
        switchMap(this.getFeatureFlagsByAccount),
        map(this.fromRawFeatureFlags),
        share(),
      );
  }

  public get isPendingState$(): Observable<boolean> {
    return this._isPendingState$.pipe(shareReplay());
  }

  public createBannerSlotEnabled(): Observable<boolean> {
    return this.accountsFeatureFlags$
      .pipe(
        map(featureFlags => !!featureFlags.slotBannerCreating),
        shareReplay(),
      );
  }

  public campaignBannerCreatingEnabled(): Observable<boolean> {
    return this.accountsFeatureFlags$
      .pipe(
        map(featureFlags => !!featureFlags.campaignBannerCreating),
        shareReplay(),
      );
  }

  public advertiserStatsAuthorizedEnabled(): Observable<boolean> {
    return this.accountsFeatureFlags$
      .pipe(
        map(featureFlags => !!featureFlags?.advertiserStatsAuthorized),
        shareReplay(),
      );
  }

  private getFeatureFlagsByAccount = (account: ICreateAccount): Observable<FeatureFlags.IRaw> => {
    this._isPendingState$.next(true);
    return this.httpClient
      .get<ItemResponseWrapper<FeatureFlags.IRaw>>(
        `${environment.backendBaseUrl}/flags/owners/${account.id}/kinds/${account.accountType.toUpperCase()}`
      )
      .pipe(
        map(this.httpUtils.entityWrapperResponseMapper),
        catchError(() => {
          return of({
            kind: account.accountType.toUpperCase(),
            accountId: '',
            ownerId: account.id,
            flags: []
          } as FeatureFlags.IRaw);
        }),
        finalize(() => this._isPendingState$.next(false)),
      );
  };

  private fromRawFeatureFlags = (featureFlagsRaw: FeatureFlags.IRaw): FeatureFlags.IEntity =>
    featureFlagsRaw.flags.reduce((acc, flagRaw) => ({
      ...acc,
      [flagRaw.flagCode]: true,
    }), {});
}
